import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { useEditDirectories } from '@repo/api/directory/useEditDirectories';
import { useEditFiles } from '@repo/api/file/useEditFiles';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useFilesStorage } from '@/store/files-store';

/**
 * Type identifier for draggable items
 */
export const FS_DRAG_TYPE = {
  FILE: 'fs-file',
  DIRECTORY: 'fs-directory',
} as const;

/**
 * Data structure attached to draggable items
 */
export interface FsDragData {
  type: typeof FS_DRAG_TYPE.FILE | typeof FS_DRAG_TYPE.DIRECTORY;
  entity: FilePublicWithPermissions | DirectoryPublic;
}

/**
 * Check if an entity is a file
 */
export function isFsFile(entity: FilePublicWithPermissions | DirectoryPublic): entity is FilePublicWithPermissions {
  return 'fileType' in entity;
}

/**
 * Options for canDropIntoDirectory function
 */
export interface CanDropOptions {
  /** Selected directories when dragging a multi-selection */
  selectedDirectories?: DirectoryPublic[];
  /** Whether the dragged item is part of the current selection */
  isDraggedItemInSelection?: boolean;
}

/**
 * Check if a directory can accept a drop
 * @param targetDirectory - The directory being dropped onto
 * @param draggedEntity - The entity being dragged
 * @param draggedEntityType - The type of the dragged entity
 * @param options - Additional options for multi-selection scenarios
 * @returns boolean indicating if the drop is allowed
 */
export function canDropIntoDirectory(
  targetDirectory: DirectoryPublic,
  draggedEntity: FilePublicWithPermissions | DirectoryPublic,
  draggedEntityType: typeof FS_DRAG_TYPE.FILE | typeof FS_DRAG_TYPE.DIRECTORY,
  options?: CanDropOptions
): boolean {
  // Target directory must have write permission
  if (!targetDirectory.permissions.write) {
    return false;
  }

  // Dragged entity must have edit permission (needed to move it)
  if (!draggedEntity.permissions.edit) {
    return false;
  }

  // Cannot drop a directory into itself
  if (draggedEntityType === FS_DRAG_TYPE.DIRECTORY && targetDirectory.id === draggedEntity.id) {
    return false;
  }

  // When dragging a selection that includes the dragged item,
  // prevent dropping onto any directory that is part of the selection
  if (options?.isDraggedItemInSelection && options?.selectedDirectories?.length) {
    const isTargetInSelection = options.selectedDirectories.some((d) => d.id === targetDirectory.id);
    if (isTargetInSelection) {
      return false;
    }
  }

  return true;
}

/**
 * Hook return type for drag drop operations
 */
export interface UseFsDragDropReturn {
  isDragging: boolean;
  draggedItem: FsDragData | null;
  handleDragStart: (data: FsDragData) => void;
  handleDragEnd: (targetDirectoryId: string | null, canceled: boolean) => Promise<void>;
  isPending: boolean;
}

/**
 * Props for the useFsDragDrop hook
 */
export interface UseFsDragDropProps {
  routeDirectoryId: string;
}

/**
 * Hook for managing file system drag and drop operations
 */
export function useFsDragDrop(routeDirectoryId: string): UseFsDragDropReturn {
  const queryClient = useQueryClient();
  const { selected, resetSelected } = useFilesStorage();

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [draggedItem, setDraggedItem] = useState<FsDragData | null>(null);

  const { executeMutations: moveFiles, isPending: isMovingFiles } = useEditFiles(routeDirectoryId);
  const { executeMutations: moveDirectories, isPending: isMovingDirectories } = useEditDirectories(routeDirectoryId);

  const isPending = useMemo<boolean>(() => {
    return isMovingFiles || isMovingDirectories;
  }, [isMovingFiles, isMovingDirectories]);

  const handleDragStart = useCallback((data: FsDragData) => {
    setIsDragging(true);
    setDraggedItem(data);
  }, []);

  const handleDragEnd = useCallback(
    async (targetDirectoryId: string | null, canceled: boolean) => {
      setIsDragging(false);
      setDraggedItem(null);

      if (canceled || !targetDirectoryId) {
        return;
      }

      // Determine what to move - either the dragged item or the entire selection
      const hasSelection = selected.files.length > 0 || selected.directories.length > 0;

      let filesToMove: FilePublicWithPermissions[] = [];
      let directoriesToMove: DirectoryPublic[] = [];

      if (hasSelection && draggedItem) {
        // Check if the dragged item is part of the selection
        const isDraggedItemSelected =
          draggedItem.type === FS_DRAG_TYPE.FILE ? selected.files.some((f) => f.id === draggedItem.entity.id) : selected.directories.some((d) => d.id === draggedItem.entity.id);

        if (isDraggedItemSelected) {
          // Move entire selection
          filesToMove = selected.files.filter((f) => f.permissions.edit && f.id !== targetDirectoryId);
          directoriesToMove = selected.directories.filter((d) => d.permissions.edit && d.id !== targetDirectoryId);
        } else {
          // Move only the dragged item
          if (draggedItem.type === FS_DRAG_TYPE.FILE && draggedItem.entity.permissions.edit) {
            filesToMove = [draggedItem.entity as FilePublicWithPermissions];
          } else if (draggedItem.type === FS_DRAG_TYPE.DIRECTORY && draggedItem.entity.permissions.edit && draggedItem.entity.id !== targetDirectoryId) {
            directoriesToMove = [draggedItem.entity as DirectoryPublic];
          }
        }
      } else if (draggedItem) {
        // No selection, move just the dragged item
        if (draggedItem.type === FS_DRAG_TYPE.FILE && draggedItem.entity.permissions.edit) {
          filesToMove = [draggedItem.entity as FilePublicWithPermissions];
        } else if (draggedItem.type === FS_DRAG_TYPE.DIRECTORY && draggedItem.entity.permissions.edit && draggedItem.entity.id !== targetDirectoryId) {
          directoriesToMove = [draggedItem.entity as DirectoryPublic];
        }
      }

      // Execute mutations
      const promises: Promise<unknown>[] = [];

      if (filesToMove.length > 0) {
        promises.push(
          moveFiles(
            filesToMove.map((file) => ({
              id: file.id,
              directoryId: targetDirectoryId,
            }))
          )
        );
      }

      if (directoriesToMove.length > 0) {
        promises.push(
          moveDirectories(
            directoriesToMove.map((directory) => ({
              id: directory.id,
              parentDirId: targetDirectoryId,
            }))
          )
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);

        // Invalidate queries to refresh the UI
        void queryClient.invalidateQueries({
          queryKey: ['directory', routeDirectoryId],
        });
        void queryClient.invalidateQueries({
          queryKey: ['directory', targetDirectoryId],
        });

        // Reset selection after successful move
        resetSelected();
      }
    },
    [draggedItem, selected, moveFiles, moveDirectories, queryClient, routeDirectoryId, resetSelected]
  );

  return {
    isDragging,
    draggedItem,
    handleDragStart,
    handleDragEnd,
    isPending,
  };
}
